import React, { useState, useRef, useEffect } from 'react';

const UploadPage = () => {
  const [file, setFile] = useState(null);
  const [uploadType, setUploadType] = useState('image');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null); // {defects, resultUrl, distinctFrames}
  const [error, setError] = useState(null);

   const [modalOpen, setModalOpen] = useState(false);
   const [modalImage, setModalImage] = useState('');
   const [modalIndex, setModalIndex] = useState(0);
   const [zoom, setZoom] = useState(1);

  const fileInputRef = useRef();
  const [dragActive, setDragActive] = useState(false);

  // Clear data on page refresh/load
  useEffect(() => {
    const clearDataOnRefresh = async () => {
      try {
        console.log('🔄 Clearing data on page refresh...');
        const response = await fetch('https://garmentguard.outworx.com/api/clear-data', {
          method: 'POST',
        });
        if (response.ok) {
          console.log('✅ Data cleared successfully on page refresh');
        } else {
          console.log('⚠️ Failed to clear data on page refresh');
        }
      } catch (error) {
        console.log('⚠️ Could not connect to backend for data clearing:', error.message);
      }
    };

    clearDataOnRefresh();
  }, []);

  // Simulate progress for video
  useEffect(() => {
    if (processing && uploadType === 'video' && progress < 100) {
      const interval = setInterval(() => {
        setProgress((p) => {
          if (p >= 100) {
            clearInterval(interval);
            setProcessing(false);
            return 100;
          }
          return Math.min(p + Math.floor(Math.random() * 10 + 5), 100);
        });
      }, 400);
      return () => clearInterval(interval);
    }
    if (progress >= 100) setProcessing(false);
  }, [processing, uploadType, progress]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    setFile(selected);
    setResult(null);
    setError(null);
    setProgress(0);
    if (selected) {
      setPreviewUrl(URL.createObjectURL(selected));
    } else {
      setPreviewUrl(null);
    }
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragleave' || e.type === 'dragover') setDragActive(true);
    else setDragActive(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange({ target: { files: e.dataTransfer.files } });
    }
  };

  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch('https://garmentguard.outworx.com/api/detect-image', {
      method: 'POST',
      body: formData,
    });
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      setResult({
        defects: data.defects || [],
        resultUrl: data.annotated_url || null,
        distinctFrames: [],
        type: 'image',
      });
    } else if (contentType && contentType.includes('image')) {
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setResult({
        defects: [],
        resultUrl: url,
        distinctFrames: [],
        type: 'image',
      });
    } else {
      setError('Unexpected response from server.');
    }
  };

  const handleVideoUpload = async (file) => {
    setProcessing(true);
    setProgress(0);
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch('https://garmentguard.outworx.com/api/detect-video', {
      method: 'POST',
      body: formData,
    });
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      setResult({
        defects: data.defects || [],
        resultUrl: data.annotated_url || data.video_url || null,
        distinctFrames: data.distinct_frames || [],
        type: 'video',
      });
      setProgress(100);
      setProcessing(false);
      
      // Auto-scroll to results after a short delay
      setTimeout(() => {
        const resultsSection = document.querySelector('[data-results-section]');
        if (resultsSection) {
          resultsSection.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 500);
    } else if (contentType && contentType.includes('video')) {
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setResult({
        defects: [],
        resultUrl: url,
        distinctFrames: [],
        type: 'video',
      });
      setProgress(100);
      setProcessing(false);
      
      // Auto-scroll to results after a short delay
      setTimeout(() => {
        const resultsSection = document.querySelector('[data-results-section]');
        if (resultsSection) {
          resultsSection.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 500);
    } else {
      setError('Unexpected response from server.');
      setProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return alert('Please upload a file before detecting.');
    setLoading(true);
    setError(null);
    setResult(null);
    setProgress(0);
    try {
      if (uploadType === 'image') {
        await handleImageUpload(file);
        setProgress(100);
      } else {
        await handleVideoUpload(file);
      }
    } catch (err) {
      setError('Detection failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Stats (mocked for demo, replace with real data if available)
  // const framesAnalyzed = result && result.type === 'video' ? 1247 : 1;
  // Use distinct frames for preview
  const allDefectImages = [
    ...((result && result.distinctFrames) || [])
  ];
  // Remove duplicates by url
  const uniqueImages = Array.from(new Map(allDefectImages.map(img => [img.url, img])).values());
  // Use uniqueImages.length for the count
  const defectsDetected = result && result.type === 'video' ? uniqueImages.length : (result ? (result.defects ? result.defects.length : 0) : 0);
  // const bestQuality = result && result.defects && result.defects.length > 0
  //   ? Math.max(...result.defects.map(d => d.confidence || 0), 0)
  //   : 0;
  // const eta = progress < 100 ? '1 min 15 sec' : 'Done';
  
    const openModal = (idx) => {
    setModalImage(uniqueImages[idx].url);
    setModalIndex(idx);
    setZoom(1);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);
  const nextImage = () => openModal((modalIndex + 1) % uniqueImages.length);
  const prevImage = () => openModal((modalIndex - 1 + uniqueImages.length) % uniqueImages.length);
  const zoomIn = () => setZoom((z) => z + 0.2);
  const zoomOut = () => setZoom((z) => Math.max(0.2, z - 0.2));

  const handleDownloadAllFrames = async () => {
    try {
      console.log('Attempting to download frames...');
      const response = await fetch('https://garmentguard.outworx.com/api/download-frames', {
        method: 'GET',
        headers: {
          'Accept': 'application/zip',
        },
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server response:', errorText);
        throw new Error(`Server responded with ${response.status}: ${errorText}`);
      }
      
      const blob = await response.blob();
      console.log('Blob size:', blob.size);
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty. No frames available to download.');
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'extracted_frames.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      alert('Images downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download images: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7fafd] flex flex-col">
      {/* Top Nav */}
      <nav className="w-full flex items-center justify-between px-8 py-2 bg-white border-b border-[#e5e7eb] shadow-sm">
        <div className="flex items-center gap-2">
          <img src="/favicon.ico" alt="logo" className="w-8 h-8 rounded-full bg-[#2563eb] p-1" />
          <span className="text-xl font-bold text-[#111827] tracking-tight">Garment Defect Detection</span>
        </div>
        <div className="flex gap-6 items-center text-[#374151] font-medium">
          <a href="#features" className="hover:text-[#2563eb]">Features</a>
          <a href="#pricing" className="hover:text-[#2563eb]">Pricing</a>
          <a href="#support" className="hover:text-[#2563eb]">Support</a>
          <button className="ml-4 px-5 py-2 bg-[#2563eb] text-white rounded-lg font-semibold shadow hover:bg-[#1746a2] transition">Sign In</button>
        </div>
      </nav>



      {/* Main Card */}
      <main className="flex-1 flex flex-col items-center py-3 px-2">
        <div className="w-full max-w-7xl bg-white rounded-2xl shadow-xl p-4 border border-[#e5e7eb] mb-3">
          <h2 className="text-3xl font-bold text-[#111827] mb-4 text-center">Upload Option {uploadType === 'image' ? 'Image' : 'Video'}</h2>

          {/* Upload type toggle */}
          <div className="flex gap-6 justify-center mb-4">
            <label className="flex items-center text-[#374151] cursor-pointer font-medium text-base">
              <input
                type="radio"
                name="uploadType"
                value="image"
                checked={uploadType === 'image'}
                onChange={() => setUploadType('image')}
                className="mr-2 accent-[#2563eb]"
              />
              Image
            </label>
            <label className="flex items-center text-[#374151] cursor-pointer font-medium text-base">
              <input
                type="radio"
                name="uploadType"
                value="video"
                checked={uploadType === 'video'}
                onChange={() => setUploadType('video')}
                className="mr-2 accent-[#2563eb]"
              />
              Video
            </label>
          </div>

          {/* Dropzone */}
          <form onSubmit={handleSubmit} onDragEnter={handleDrag} className="w-full">
            <div
              className={`w-full border-2 border-dashed rounded-xl flex flex-col items-center justify-center py-2 px-4 mb-4 transition ${dragActive ? 'border-[#2563eb] bg-[#f0f6ff]' : 'border-[#e5e7eb] bg-[#f7fafd]'}`}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              style={{ cursor: 'pointer' }}
            >
              <svg xmlns="https://www.w3.org/2000/svg" className="h-14 w-14 text-[#9ca3af] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4m0 0V8m0 4h4m-4 0H8m12 4a4 4 0 01-4 4H8a4 4 0 01-4-4V8a4 4 0 014-4h8a4 4 0 014 4v8z" />
              </svg>
              <p className="text-lg text-[#374151] font-medium mb-1">Drop your {uploadType === 'image' ? 'image' : 'video'} file here or click to browse</p>
              <p className="text-sm text-[#6b7280] mb-4">Supports {uploadType === 'image' ? 'JPG, PNG, JPEG' : 'MP4, AVI, MOV'} files up to 100MB</p>
              <input
                ref={fileInputRef}
                type="file"
                accept={uploadType === 'image' ? 'image/*' : 'video/*'}
                onChange={handleFileChange}
                className="hidden"
              />
              <button
                type="button"
                className="mt-2 px-6 py-2 bg-[#2563eb] text-white rounded-lg font-semibold shadow hover:bg-[#1746a2] transition"
                onClick={e => { e.stopPropagation(); fileInputRef.current && fileInputRef.current.click(); }}
              >Choose File</button>
            </div>



            {/* Preview */}
            {previewUrl && (
              <div className="mb-4 text-center">
                <span className="block text-[#374151] mb-3 font-medium text-lg">Preview:</span>
                {uploadType === 'image' ? (
                  <img src={previewUrl} alt="preview" className="mx-auto rounded-xl border border-[#e5e7eb] max-h-64 shadow" />
                ) : (
                  <video controls src={previewUrl} className="mx-auto rounded-xl border border-[#e5e7eb] max-h-64 shadow" />
                )}
              </div>
            )}

            <button
              type="submit"
              className="w-full py-3 px-4 bg-[#2563eb] hover:bg-[#1746a2] text-white font-bold rounded-xl shadow-lg transition duration-200 text-m tracking-wide uppercase"
              disabled={loading || processing}
            >
              {loading || processing ? 'Detecting...' : 'Upload and Detect'}
            </button>
          </form>
        </div>

        {/* Progress and Stats */}
        {(loading || processing || (result && progress === 100)) && (
          <div className="w-full max-w-7xl bg-white rounded-2xl shadow p-4 border border-[#e5e7eb] mb-4">
            <div className="text-xl font-bold text-[#111827] mb-4">Processing Video</div>
            <div className="w-full bg-[#e5e7eb] rounded-full h-4 mb-4">
              <div
                className="bg-[#2563eb] h-4 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-[#374151] text-lg font-medium mb-2">
              <div>Extracting frames from video... {progress}%</div>
              {/* <div>ETA: {eta}</div> */}
            </div>
            <div className="flex justify-between text-[#2563eb] text-2xl font-bold mt-4">
              {/* <div>{framesAnalyzed} <span className="text-base font-medium text-[#374151]">Frames Analyzed</span></div> */}
              <div>{defectsDetected} <span className="text-base font-medium text-[#374151]">Defects Detected</span></div>
              {/* <div>{bestQuality ? bestQuality : '--'} <span className="text-base font-medium text-[#374151]">Best Quality</span></div> */}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="w-full max-w-3xl bg-red-100 text-red-700 rounded-xl p-6 mb-8 border border-red-300 text-center font-semibold">
            {error}
          </div>
        )}

        {/* Results Section */}
        {result && progress === 100 && (
          <div className="w-full max-w-7xl bg-white rounded-2xl shadow px-32 py-4 border border-[#e5e7eb]" data-results-section>
            <div className="font-bold text-2xl text-[#111827] mb-2">
              {result.type === 'video' ? 'Extracted Frames' : 'Detection Result'}
            </div>

            {/* Video: Show frames as cards */}
            {result.type === 'video' && result.distinctFrames && result.distinctFrames.length > 0 && (
              <>
                <div className="mb-4 flex justify-between items-center flex-wrap gap-2">
                  <div className="text-[#2563eb] font-semibold text-lg">Best quality frames detected from your video</div>
                  <button
                    className="px-6 py-2 bg-[#2563eb] text-white rounded-xl font-bold shadow-md text-sm sm:text-base border border-[#2563eb] hover:bg-[#1746a2] transition"
                    onClick={handleDownloadAllFrames}
                  >
                    Download All
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                  {result.distinctFrames.map((frame, idx) => (
                    <div key={idx} className="bg-[#f7fafd] rounded-xl border border-[#e5e7eb] shadow flex flex-col items-center p-4" style={{ minHeight: '260px', minWidth: '180px', maxWidth: '220px' }}>
                      <img src={frame.url} alt={frame.defect ? frame.defect : `Frame ${idx + 1}`} onClick={() => openModal(idx)} className="w-full h-80 object-cover rounded-lg mb-3 border border-[#e5e7eb]" style={{ objectFit: 'cover', height: '160px', width: '100%' }} />
                      <div className="font-semibold text-[#111827] text-base mb-1">{frame.defect ? frame.defect : `Frame #${idx + 1}`}</div>

                      
                    </div>
                  ))}
                </div>

              </> 
            )}
            {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white p-10 rounded-xl max-w-4xl w-full relative flex flex-col items-center">
            <button className="absolute text-xl font-extrabold top-3 right-3 text-black hover:text-gray-600" onClick={closeModal}>✕</button>
            <div className="flex w-full items-center justify-between mb-2" style={{ maxWidth: '700px' }}>
              <button 
                onClick={prevImage} 
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-all font-semibold"
                style={{ minWidth: '80px' }}
              >
                ⟵ Prev
              </button>
              <div className="relative inline-block">
                <img
                  src={modalImage}
                  alt="Zoomed"
                  style={{ transform: `scale(${zoom})`, border: '4px solid red' }}
                  className="rounded max-h-[70vh] mx-auto transition-transform duration-200"
                />
              </div>
              <button 
                onClick={nextImage} 
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-all font-semibold"
                style={{ minWidth: '80px' }}
              >
                Next ⟶
              </button>
            </div>
            {/* Controls row: Zoom and Download */}
            <div className="mt-4 flex justify-center items-center gap-4 w-full" style={{ maxWidth: '700px' }}>
              <button 
                onClick={zoomIn} 
                className="bg-gray-300 text-gray-700 px-3 py-2 rounded hover:bg-gray-400 transition-all font-semibold"
              >
                Zoom +
              </button>
              <button 
                onClick={zoomOut} 
                className="bg-gray-300 text-gray-700 px-3 py-2 rounded hover:bg-gray-400 transition-all font-semibold"
              >
                Zoom -
              </button>
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(modalImage);
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `frame_${modalIndex + 1}.jpg`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    alert('Image downloaded successfully!');
                  } catch (error) {
                    alert('Failed to download image: ' + error.message);
                  }
                }}
                className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors font-semibold"
              >
                Download Image
              </button>
            </div>
          </div>
        </div>
      )}
            {/* Image: Show result image */}
            {result.type === 'image' && result.resultUrl && (
              <div className="flex flex-col items-center">
                <img src={result.resultUrl} alt="Detection Result" className="rounded-xl border border-[#e5e7eb] max-h-96 shadow mb-4" />
                <div className="font-semibold text-[#111827] text-lg">Detection Result</div>
              </div>
            )}
            {/* No results */}
            {((result.type === 'video' && (!result.distinctFrames || result.distinctFrames.length === 0)) || (result.type === 'image' && !result.resultUrl)) && (
              <div className="text-center text-[#374151] text-lg font-medium py-8">No defects detected or no result to display.</div>
            )}
          </div>

        )}
      </main>
      {/* Help button bottom right */}
      <a href="#help" className="fixed bottom-6 right-6 bg-white border border-[#2563eb] text-[#2563eb] rounded-full shadow-lg px-5 py-2 font-semibold text-base flex items-center gap-2 hover:bg-[#2563eb] hover:text-white transition">
        <svg xmlns="https://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 14h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8s-9-3.582-9-8 4.03-8 9-8 9 3.582 9 8z" />
        </svg>
        Help
      </a>
    </div>
  );
};

export default UploadPage;













